/**
 * Canonical metadata for the four fixed Automations (design: 2026-06-26 Automations Module Revamp).
 *
 * Every workspace is seeded with exactly four preset `sub_agents` kinds; this surface renders one
 * non-deletable card per kind with a stable label, one-line description, and icon. The map is keyed
 * by the backend `triggerConfig.kind` so it stays correct regardless of the row's stored `name`.
 *
 * Note: the `overnight_processing` kind is presented as "Overnight Processing" — the user-facing rename
 * lands here in Phase 1; the backing kind/engine is repurposed in Phase 3 (slot migration).
 */
import type { ReactNode } from "react";
import type { PresetKind } from "./redesign/fixtures";

export interface PresetMeta {
  /** User-facing name shown on the card and Customize panel. */
  label: string;
  /** One-line description of what the automation does. */
  description: string;
  /** Card icon. */
  icon: ReactNode;
  /** Per-automation accent colour (icon tile + on-state rail). */
  accent: string;
}

function Icon({ children }: { children: ReactNode }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const EnvelopeIcon = (
  <Icon>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m2 7 10 7 10-7" />
  </Icon>
);

const SunIcon = (
  <Icon>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
  </Icon>
);

const MoonIcon = (
  <Icon>
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </Icon>
);

const CalendarIcon = (
  <Icon>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </Icon>
);

export const PRESET_META: Record<PresetKind, PresetMeta> = {
  inbox_watcher: {
    label: "Email Auto-Draft Responder",
    description:
      "Drafts replies to real senders for your review — never sends on its own.",
    icon: EnvelopeIcon,
    accent: "#a8546a",
  },
  daily_briefing: {
    label: "Daily Brief",
    description: "Your morning rundown, plus fresh web news on topics you pick.",
    icon: SunIcon,
    accent: "#b4763f",
  },
  overnight_processing: {
    label: "Overnight Processing",
    description:
      "Researches what you’re working on overnight and reports back each morning.",
    icon: MoonIcon,
    accent: "#7a5ea8",
  },
  meeting_prep: {
    label: "Meeting Prep",
    description: "A full prep pack before each meeting, formatted how you like.",
    icon: CalendarIcon,
    accent: "#4f8a6b",
  },
};

/** Canonical top-to-bottom order of the four cards. */
export const PRESET_ORDER: PresetKind[] = [
  "inbox_watcher",
  "daily_briefing",
  "overnight_processing",
  "meeting_prep",
];
